import { Benefit, ClaimPropertiesResponse, useClaimTypesProperties } from 'all-in-one-shared-api';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { useCachedClaimProperties, useKeycloakReady } from 'hooks';
import { useMemo, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isLocaleEnglish } from 'utils/languageUtils';
import { ClaimFormState } from '../schema';
import { SUBNODE_PATTERN } from '../components/ClaimInfoCard/ClaimInfoCard';
import { useProfileInfoContext } from 'contexts/profileInfo';

export interface SelectedClaimTypeObjectType {
  type: 'OP' | 'other' | '';
  data: Benefit[];
  requiredDocs: (string | undefined)[];
  isLoaded?: boolean;
}

interface UseSelectedClaimTypeMappingProps {
  methods: UseFormReturn<ClaimFormState>;
}

const getRequiredDocs = (claims: Benefit[], language: string) => {
  return [
    ...new Set(
      claims.flatMap((o) => (isLocaleEnglish(language) ? o.requiredAttachments : o.chineseRequiredAttachments)),
    ),
  ];
};

export const getRequiredDocsBasedOnClaimType = (
  selectedClaimType: string,
  selectedBenefits: string[],
  claimProperties: ClaimPropertiesResponse | undefined,
  language: string,
): SelectedClaimTypeObjectType => {
  console.log('selectedClaimType in mapping function', selectedClaimType, selectedClaimType.match(SUBNODE_PATTERN));
  if (selectedClaimType.match(SUBNODE_PATTERN) || selectedClaimType === 'OP') {
    const benefitsData = claimProperties?.claimTypes?.find((o) => o.code === 'OP')?.benefits;

    const claims = selectedBenefits.map(
      (benefitCode: string) =>
        benefitsData?.find((benefit) => `__${benefit.code}` === benefitCode || benefit.code === benefitCode) || {
          code: benefitCode,
        },
    );
    return {
      type: 'OP',
      data: claims,
      requiredDocs: getRequiredDocs(claims, language),
    };
  }

  const claims = claimProperties?.claimTypes?.filter((o) => selectedClaimType.includes(o?.code || '')) ?? [];
  return {
    type: 'other',
    data: claims,
    requiredDocs: getRequiredDocs(claims, language),
  };
};

export const useSelectedClaimTypeMapping = ({ methods }: UseSelectedClaimTypeMappingProps) => {
  const { i18n } = useTranslation();
  const selectedClaimType: string[] | undefined = methods.watch('claimTypeObject');
  const patientMemberType = methods.watch('patientMemberType');
  const previousClaimType = useRef<string>();

  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);
  const { token, ready } = useKeycloakReady();
  const { state: profileInfo } = useProfileInfoContext();

  const { shouldFetch: shouldFetchClaimTypesProperties, data: claimTypesPropertiesData } = useClaimTypesProperties(
    {
      key: 'getClaimTypesProperties',
      params: {
        policyNumber: profileInfo?.policyNumber,
        memberId: profileInfo?.memberId,
        patientMemberType,
      },
    },
    token ?? '',
    profileInfo?.policyNumber ?? '',
    profileInfo?.memberId ?? '',
    patientMemberType,
  );

  useEffect(() => {
    shouldFetchClaimTypesProperties(ready && profileInfo !== undefined && patientMemberType !== undefined);
  }, [ready, patientMemberType, profileInfo, shouldFetchClaimTypesProperties]);

  useEffect(() => {
    if (claimProperties) {
      const { familyMembers } = claimProperties;
      if ((familyMembers || []).length === 1) {
        const principalMember = familyMembers?.[0] || {};
        methods.setValue('patientName', `${principalMember.firstName} ${principalMember.lastName}`);
      }
    }
  }, [claimProperties, claimProperties?.familyMembers, methods, methods.setValue]);

  const selectedClaimTypeObject: SelectedClaimTypeObjectType = useMemo(() => {
    if (!selectedClaimType || !selectedClaimType.length || !claimTypesPropertiesData) {
      return {
        type: '',
        data: [],
        requiredDocs: [''],
        isLoaded: !!claimTypesPropertiesData,
      };
    }

    const benefits = selectedClaimType[0].match(SUBNODE_PATTERN) ? selectedClaimType : selectedClaimType.slice(1);
    const selectedClaimTypeObjectType = getRequiredDocsBasedOnClaimType(
      selectedClaimType[0],
      benefits,
      claimTypesPropertiesData,
      i18n.language,
    );
    selectedClaimTypeObjectType.isLoaded = true;

    return selectedClaimTypeObjectType;
  }, [selectedClaimType, claimTypesPropertiesData, i18n.language]);

  // useMemo 只做纯计算，setValue 放到 useEffect 中执行，避免 render 期间触发 state 更新
  useEffect(() => {
    if (!selectedClaimTypeObject.type) {
      methods.setValue('claims', []);
      return;
    }
    methods.setValue('claimType', selectedClaimTypeObject.type);
    methods.setValue('claims', selectedClaimTypeObject.data);
    methods.setValue('requiredDocs', selectedClaimTypeObject.requiredDocs);
  }, [selectedClaimTypeObject, methods]);

  useEffect(() => {
    if (previousClaimType.current && previousClaimType.current !== methods.getValues('claimType')) {
      methods.clearErrors();
      methods.setValue('receiptAmounts', ['']);
    }
    previousClaimType.current = methods.getValues('claimType');
  }, [selectedClaimTypeObject.type, methods]);

  return { selectedClaimTypeObject };
};
